/**
 * Contact contracts — slice 1, "contacts in".
 *
 * A contact is **dataset-owned**: it exists because one row of one upload
 * created it. The same person appearing in two uploads is two contact rows, and
 * that is deliberate — a row carries its own upload's columns, which a template
 * personalises from. Since slice 58 both rows point at the same person
 * (`person_id`, chosen by the database's merge rule in `0041_people.sql`), and
 * that is where the merging happens: never in a contact row. Nothing in this file
 * should ever grow a "merge" or "upsert" shape.
 *
 * `extras` is the other half of the slice's promise: every source column that
 * was not mapped onto name / email / phone is preserved there under the
 * detected column's name, so an import never loses data it was handed.
 */
import { z } from 'zod';

import {
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  sortDirectionSchema,
  uuidSchema,
} from '../common.js';

/* -------------------------------------------------------------------------- */
/* Extras                                                                      */
/* -------------------------------------------------------------------------- */

/** Extras keys are truncated to this, matching `detectedColumn.name`. */
export const EXTRAS_KEY_MAX_LENGTH = 200;

/** Values longer than this are truncated on import rather than rejecting a row. */
export const EXTRAS_VALUE_MAX_LENGTH = 2000;

/**
 * One preserved cell. The parser's own reading of the cell is kept — a number
 * stays a number and a blank stays `null` — so a later segment rule can compare
 * numerically instead of re-parsing text. Dates are stored as ISO-8601 strings.
 */
export const extraValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type ExtraValue = z.infer<typeof extraValueSchema>;

/** Every unmapped source column, keyed by the detected column's `name`. */
export const contactExtrasSchema = z.record(z.string(), extraValueSchema);
export type ContactExtras = z.infer<typeof contactExtrasSchema>;

/* -------------------------------------------------------------------------- */
/* Read                                                                        */
/* -------------------------------------------------------------------------- */

export const contactSchema = z.object({
  id: uuidSchema,
  /** The upload this contact came from. Contacts never outlive their dataset. */
  datasetId: uuidSchema,
  name: z.string().nullable(),
  /** Normalised (trimmed, lower-cased) on import. */
  email: z.string().nullable(),
  /** E.164, e.g. `+919812345678`. Null when the row carried no usable phone. */
  phoneE164: z.string().nullable(),
  /** Unmapped source columns, verbatim. */
  extras: contactExtrasSchema,
  createdAt: isoDateTimeSchema,
});
export type Contact = z.infer<typeof contactSchema>;

export const CONTACT_SORT_VALUES = ['createdAt', 'name', 'email'] as const;
export const contactSortSchema = z.enum(CONTACT_SORT_VALUES);
export type ContactSort = z.infer<typeof contactSortSchema>;

export const contactListQuerySchema = paginationQuerySchema.extend({
  /** Restricts the list to one upload. Omitted, it spans the whole tenant. */
  datasetId: uuidSchema.optional(),
  /** Case-insensitive substring of name, email or phone. */
  search: z.string().trim().max(120).optional(),
  sort: contactSortSchema.default('createdAt'),
  direction: sortDirectionSchema.default('desc'),
});
export type ContactListQuery = z.infer<typeof contactListQuerySchema>;

export const contactListResponseSchema = paginated(contactSchema);
export type ContactListResponse = z.infer<typeof contactListResponseSchema>;
